import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, type Forum } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";

const STALE_AFTER_MS = 5 * 60 * 1000; // 5 min

interface Props {
  forumId: number | string;
  className?: string;
  /**
   * When true the component checks the cached forum's `last_price_at` on
   * mount ; if the data is older than STALE_AFTER_MS it triggers a refresh
   * automatically. The user can still click the button at any time to force
   * a refetch.
   */
  autoRefresh?: boolean;
}

/**
 * Single-click refresh for a forum's price + news + history. The Go API
 * fetches everything in parallel and returns a consolidated payload that
 * we write straight back into the React Query caches — no polling.
 */
export function RefreshAllButton({ forumId, className, autoRefresh = true }: Props) {
  const qc = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [auto, setAuto] = useState(false);
  const didAutoRun = useRef(false);

  const m = useMutation({
    mutationFn: () => api.refreshForumAllSync(forumId),
    onSuccess: (res) => {
      // Patch every forums list variant (["forums","all"], ["forums","hot"], …).
      qc.setQueriesData({ queryKey: ["forums"] }, (prev: any) => {
        if (!Array.isArray(prev)) return prev;
        return prev.map((f: Forum) =>
          String(f.id) === String(forumId)
            ? {
                ...f,
                title: res.price.title || f.title,
                last_price: res.price.has_price ? res.price.last_price : f.last_price,
                last_variation_pct: res.price.has_price
                  ? res.price.last_variation_pct
                  : f.last_variation_pct,
                price_currency: res.price.price_currency || f.price_currency,
                isin: res.price.isin || f.isin,
                symbol: res.price.symbol || f.symbol,
                last_price_at: res.price.observed_at,
              }
            : f
        );
      });
      // Replace the news + history caches outright.
      qc.setQueryData(["forum-news", forumId], res.news.items);
      qc.setQueryData(["forum-history", forumId], res.history.rows);

      if (!auto) {
        // Don't toast on auto-runs (they happen silently on mount).
        toast.success("Forum mis à jour");
      }
    },
    onError: (e) => {
      if (auto) {
        // Silent on auto-runs ; the user didn't ask for this.
        // eslint-disable-next-line no-console
        console.warn("[auto-refresh-all]", e);
      } else {
        toast.error(`Erreur : ${e instanceof Error ? e.message : "?"}`);
      }
    },
    onSettled: () => setAuto(false),
  });

  // Auto-refresh on mount if the cached price is older than 5 min.
  const maybeAutoRun = useCallback(() => {
    // The refresh-all endpoint is auth-gated server-side (RequireUser). Don't
    // auto-trigger it for anonymous visitors — it would 401.
    if (!isAuthenticated || !autoRefresh || didAutoRun.current) return;
    didAutoRun.current = true;
    // Search across every cached forums variant (["forums","all"],
    // ["forums","hot"], …) to find this forum.
    let f: Forum | undefined;
    for (const [, list] of qc.getQueriesData<Forum[]>({ queryKey: ["forums"] })) {
      if (!Array.isArray(list)) continue;
      const match = list.find((x) => String(x.id) === String(forumId));
      if (match) {
        f = match;
        break;
      }
    }
    const stamps = [
      f?.last_price_at,
    ].filter((s): s is string => !!s);
    const newest = stamps
      .map((s) => new Date(s).getTime())
      .reduce<number>((acc, n) => (n > acc ? n : acc), 0);
    const age = newest > 0 ? Date.now() - newest : Infinity;
    if (age > STALE_AFTER_MS) {
      setAuto(true);
      m.mutate();
    }
  }, [isAuthenticated, autoRefresh, qc, forumId, m]);

  useEffect(() => {
    maybeAutoRun();
  }, [maybeAutoRun]);

  const busy = m.isPending;

  // The endpoint requires auth — don't expose the button to anonymous visitors.
  if (!isAuthenticated) return null;

  return (
    <Button
      variant="default"
      size="sm"
      onClick={() => {
        setAuto(false);
        m.mutate();
      }}
      disabled={busy}
      className={cn(className)}
      title="Met à jour le cours, les news et l'historique en une seule fois"
    >
      {busy ? <Loader2 className="size-3.5 animate-spin" /> : <RefreshCw className="size-3.5" />}
      {busy ? (auto ? "Mise à jour auto…" : "Mise à jour…") : "Tout rafraîchir"}
    </Button>
  );
}
